// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * AdvancedSettingsModal - Modal for detailed game settings
 *
 * Tabbed interface with real-time validation and settings controls.
 */

import { useState } from 'react';
import { logger } from '../../lib/logger';
import { validateSettingsBatch } from '../../lib/validation/settings';
import { Button } from '../Button';
import { Modal } from '../Modal';
import { getControlForType } from './controls/registry';
import type { ControlValue } from './controls/BaseControl';
import type { LobbySettings, GameConfigSchema } from '../../types/game';

interface AdvancedSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameId: string;
  currentSettings: LobbySettings;
  configSchema: GameConfigSchema;
  currentPlayerCount?: number;
  onSave: (settings: Partial<LobbySettings>) => void;
}

type Tab = 'players' | 'scoring' | 'mechanics';

export function AdvancedSettingsModal({
  isOpen,
  onClose,
  gameId,
  currentSettings,
  configSchema,
  currentPlayerCount,
  onSave,
}: AdvancedSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('players');
  const [pendingSettings, setPendingSettings] = useState<Partial<LobbySettings>>({});

  const getSchemaScopedSettings = (settings: Partial<LobbySettings>) =>
    Object.fromEntries(Object.entries(settings).filter(([key]) => key in configSchema));

  const errors =
    Object.keys(pendingSettings).length === 0
      ? {}
      : validateSettingsBatch(
          {
            ...getSchemaScopedSettings(currentSettings),
            ...getSchemaScopedSettings(pendingSettings),
          },
          configSchema,
          currentPlayerCount
        );

  const updateSetting = (key: string, value: ControlValue) => {
    logger.debug('advanced_setting_changed', { key, value });
    setPendingSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleApply = () => {
    if (Object.keys(errors).length > 0) {
      logger.warn('cannot_apply_settings_with_errors', { errors });
      return;
    }

    logger.info('applying_advanced_settings', { pendingSettings });
    onSave(pendingSettings);
    setPendingSettings({});
    setActiveTab('players');
    onClose();
  };

  const handleReset = () => {
    logger.info('resetting_advanced_settings');
    setPendingSettings({});
  };

  const handleCancel = () => {
    logger.debug('advanced_settings_cancelled');
    setPendingSettings({});
    setActiveTab('players');
    onClose();
  };

  // Get effective value (pending or current)
  const getValue = (key: string): ControlValue => {
    const val = key in pendingSettings ? pendingSettings[key] : currentSettings[key];
    return val ?? null;
  };

  // Categorize settings by tab
  const playerSettings = ['max_players', 'min_players'];
  const scoringSettings = ['score_to_win', 'hand_size', 'max_rounds'];
  const mechanicsSettings = Object.keys(configSchema).filter(
    key => !playerSettings.includes(key) && !scoringSettings.includes(key)
  );

  const renderSetting = (key: string) => {
    const config = configSchema[key];
    if (!config) return null;

    // Use control registry to get appropriate component
    const ControlComponent = getControlForType(config.type);
    if (!ControlComponent) {
      logger.warn('no_control_for_type', { type: config.type, key });
      return null;
    }

    const value = getValue(key);
    const error = errors[key];

    // Format label from key
    const label = key
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    // Determine step based on type - floats need smaller step
    const step = config.type === 'float' ? 0.01 : 1;

    return (
      <ControlComponent
        key={key}
        label={label}
        value={value}
        description={config.description}
        error={error}
        onChange={val => updateSetting(key, val)}
        config={config}
        gameId={gameId}
        min={config.min}
        max={config.max}
        step={step}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title="Advanced Settings">
      <div className="space-y-4" data-testid="advanced-settings-modal">
        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/20">
          {(['players', 'scoring', 'mechanics'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
                activeTab === tab
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
              type="button"
              data-testid={`settings-tab-${tab}`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[300px]">
          {activeTab === 'players' && (
            <div data-testid="settings-tab-content-players">
              {playerSettings.map(key => renderSetting(key))}
              {playerSettings.every(key => !configSchema[key]) && (
                <p className="text-sm text-gray-400">No player settings available</p>
              )}
            </div>
          )}

          {activeTab === 'scoring' && (
            <div data-testid="settings-tab-content-scoring">
              {scoringSettings.map(key => renderSetting(key))}
              {scoringSettings.every(key => !configSchema[key]) && (
                <p className="text-sm text-gray-400">No scoring settings available</p>
              )}
            </div>
          )}

          {activeTab === 'mechanics' && (
            <div data-testid="settings-tab-content-mechanics">
              {mechanicsSettings.map(key => renderSetting(key))}
              {mechanicsSettings.length === 0 && (
                <p className="text-sm text-gray-400">No game mechanics settings available</p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3 pt-4 border-t border-white/20">
          <Button
            onClick={handleReset}
            variant="secondary"
            className="flex-1"
            data-testid="settings-reset-button"
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleCancel}
            variant="secondary"
            className="flex-1"
            data-testid="settings-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="success"
            className="flex-1"
            disabled={Object.keys(errors).length > 0}
            data-testid="settings-apply-button"
          >
            Apply
          </Button>
        </div>

        {/* Error Summary */}
        {Object.keys(errors).length > 0 && (
          <div
            className="bg-red-500/20 border border-red-500 rounded-lg p-3"
            data-testid="settings-errors"
          >
            <p className="text-sm font-medium text-red-400 mb-2">
              Please fix the following errors:
            </p>
            <ul className="text-sm text-red-300 list-disc list-inside">
              {Object.entries(errors).map(([key, message]) => (
                <li key={key}>{message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
